import React from 'react';
import { Users, Code, Shield, Building } from 'lucide-react';

interface Props {
  onSelect: (role: string) => void;
}

const RoleSelection: React.FC<Props> = ({ onSelect }) => {
  const roles = [
    {
      id: 'manager_public',
      title: 'Public Sector Manager',
      description: 'AI system manager in government or public organizations',
      icon: Building,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      hoverColor: 'hover:border-blue-500',
    },
    {
      id: 'manager_internal',
      title: 'Private Sector Manager',
      description: 'AI system manager in private companies',
      icon: Users,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      hoverColor: 'hover:border-green-500',
    },
    {
      id: 'developer_public',
      title: 'Public Sector Developer',
      description: 'AI developer working in government or public organizations',
      icon: Code,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      hoverColor: 'hover:border-purple-500',
    },
    {
      id: 'developer_internal',
      title: 'Private Sector Developer',
      description: 'AI developer working in private companies',
      icon: Shield,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      hoverColor: 'hover:border-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-8">
      <div className="max-w-6xl mx-auto fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="gradient-text">Select Your Role</span>
          </h1>
          <p className="text-xl text-gray-600">
            Choose the role that best describes your position
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold">
              ✓
            </div>
            <span className="text-sm font-medium text-gray-700">Landing</span>
          </div>
          <div className="w-16 h-1 bg-green-500"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold">
              ✓
            </div>
            <span className="text-sm font-medium text-gray-700">Framework</span>
          </div>
          <div className="w-16 h-1 bg-indigo-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold">
              3
            </div>
            <span className="text-sm font-medium text-gray-700">Role</span>
          </div>
          <div className="w-16 h-1 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center font-semibold">
              4
            </div>
            <span className="text-sm font-medium text-gray-400">Assessment</span>
          </div>
        </div>

        {/* Role Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <div
                key={role.id}
                className={`bg-white rounded-2xl shadow-lg p-8 border-2 border-transparent transition-all screen-card ${role.hoverColor}`}
                onClick={() => onSelect(role.id)}
              >
                <div className={`w-16 h-16 ${role.color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className="w-9 h-9 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold mb-2">{role.title}</h3>
                <p className="text-gray-600 text-lg">{role.description}</p>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">ℹ️</span>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-2 text-indigo-900">
                Why does your role matter?
              </h4>
              <p className="text-indigo-800">
                Different roles have different responsibilities and perspectives when it comes to AI systems.
                Selecting your role helps us tailor the assessment questions to be most relevant to your
                specific context and responsibilities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleSelection;
